import {
  type GeoSpaceChiban,
  type GeoSpaceReverseApiError,
} from "@/features/map/types";
import {
  type RegistrationMapLookupResponse,
  type RegistrationMapDetail,
} from "@/features/map/types/registrationMap";
import {
  type TrustartLookupResponse,
  type TrustartParcelResult,
} from "@/features/map/types/trustart";
import { type SearchSourceId } from "@/features/map/utils/buildSearchChain";
import {
  type SourceLookup,
  type SourceOutcome,
} from "@/features/map/utils/runSearchChain";
import { useGeoSpaceApi } from "@/features/map/hooks/useGeospaceSearch";
import {
  useRegistrationMapApi,
  registrationMapDetailToGeoSpaceChiban,
  isRegistrationMapRateLimitError,
} from "@/features/map/hooks/useRegistrationMapApi";
import {
  useTrustartParcelApi,
  trustartParcelToGeoSpaceChiban,
} from "@/features/map/hooks/useTrustartParcelApi";
import {
  useZenrinApi,
  zenrinToGeoSpaceChiban,
  zenrinListToGeoSpaceChiban,
} from "@/features/map/hooks/useZenrinSearch";
import { validateZenrinWordQuery } from "@/features/map/utils/zenrinWordValidation";

/** TRUSTART / 登記所備付が message を返さなかった場合の非提供エリアの既定文言。 */
const NOT_AVAILABLE_FALLBACK_MESSAGE = "ご指定のエリアは対応範囲外です。";

/** 図形検索（円 / ポリゴン）に対応しているデータソース。 */
export const SHAPE_CAPABLE_SOURCES: ReadonlySet<SearchSourceId> =
  new Set<SearchSourceId>([
    "geospaceChiban",
    "geospaceLegalAffairs",
    "trustartChiban",
    "registrationMap",
    "zenrin",
  ]);

/**
 * 登記地番検索で、住居表示を伴わないヒットを採用しないデータソース。
 *
 * GEOSPACE / ゼンリンは住居表示が返らないと結果として不十分なため後段へ流し、
 * 役所系は地番だけでも成立する、という既存の判定差をそのまま引き継ぐ。
 */
export const REQUIRES_ADDRESS_SOURCES: ReadonlySet<SearchSourceId> =
  new Set<SearchSourceId>(["geospaceChiban", "geospaceLegalAffairs", "zenrin"]);

/** 図形検索の対象。円か多角形かで BE のエンドポイントが分かれる。 */
export type SearchShape =
  | { kind: "circle"; radius: number; lat: number; lng: number }
  | { kind: "polygon"; polygon: string };

/** GEOSPACE / ゼンリンの「結果があればヒット」を共通の outcome に変換する。 */
const toOutcome = (result: GeoSpaceChiban | undefined | null): SourceOutcome =>
  result ? { kind: "hit", result } : { kind: "miss" };

/**
 * GEOSPACE の住居表示検索（地番地図・法務省地番地図）の例外を振り分ける。
 *
 * 400 は入力内容の誤りなので、案内文言を出すために呼び出し側へ通す。
 * それ以外（5xx・通信断など）は外れとして扱い、後段の役所系・ゼンリンへ流す。
 */
const passThroughBadRequest = async (
  lookup: () => Promise<SourceOutcome>
): Promise<SourceOutcome> => {
  try {
    return await lookup();
  } catch (error) {
    if ((error as GeoSpaceReverseApiError).response?.status === 400) {
      throw error;
    }
    return { kind: "miss" };
  }
};

/**
 * 登記所備付地図の検索 3 本のレート制限（429）を、例外ではなく outcome として返す。
 *
 * 例外のまま投げると、呼び出し側の catch が Google フォールバックへ進んでしまう。
 * outcome にすればチェーンがそこで止まり、後段のゼンリン等も呼ばれない。
 */
const catchRateLimit = async (
  lookup: () => Promise<SourceOutcome>
): Promise<SourceOutcome> => {
  try {
    return await lookup();
  } catch (error) {
    if (isRegistrationMapRateLimitError(error)) return { kind: "rateLimited" };
    throw error;
  }
};

/** 範囲検索の結果。打ち切りを返すのは登記所備付地図だけで、他のソースは常に false。 */
export interface ShapeSearchResult {
  results: GeoSpaceChiban[];
  /** 該当筆が返却上限を超え、results が打ち切られたか。 */
  truncated: boolean;
}

const untruncated = (
  results: GeoSpaceChiban[] | undefined
): ShapeSearchResult | undefined =>
  results ? { results, truncated: false } : undefined;

/**
 * 役所系 2 ソースの envelope を共通の outcome に変換する。
 *
 * 筆は外れたが住所の geocode には成功したケース（not_found + geocoded）を拾うことで、
 * BE がジオコーディング済みの座標にピンを置け、FE から Google を呼び直さずに済む。
 * failed は住所検索（reverse）で BE の Google ジオコーディングが失敗したときだけ返る。
 */
const envelopeToOutcome = <TResult>(
  response:
    | (Omit<TrustartLookupResponse, "result"> & { result?: TResult })
    | undefined,
  toChiban: (result: TResult) => GeoSpaceChiban
): SourceOutcome => {
  if (!response) return { kind: "miss" };

  if (response.status === "success" && response.result) {
    return { kind: "hit", result: toChiban(response.result) };
  }
  if (response.status === "not_found" && response.geocoded) {
    return { kind: "geocoded", geocoded: response.geocoded };
  }
  if (response.status === "failed") {
    return { kind: "geocodeFailed" };
  }
  if (response.status === "not_available") {
    return {
      kind: "notAvailable",
      message: response.message ?? NOT_AVAILABLE_FALLBACK_MESSAGE,
    };
  }
  return { kind: "miss" };
};

export interface ParcelSearchSources {
  /** 座標から筆を引く lookup 群（クリック検索用）。 */
  buildLatLngLookup: (
    lat: number,
    lng: number
  ) => Partial<Record<SearchSourceId, SourceLookup>>;
  /** 登記地番から筆を引く lookup 群。 */
  buildChibanLookup: (
    chiban: string,
    options?: { onZenrinRejected?: (message: string) => void }
  ) => Partial<Record<SearchSourceId, SourceLookup>>;
  /** 住居表示から筆を引く lookup 群。 */
  buildAddressLookup: (
    address: string,
    options?: { onZenrinRejected?: (message: string) => void }
  ) => Partial<Record<SearchSourceId, SourceLookup>>;
  /** 図形の範囲内にある筆の一覧を引く。通信に失敗したときは undefined を返す。 */
  runShapeSearch: (
    sourceId: SearchSourceId,
    shape: SearchShape,
    signal?: AbortSignal
  ) => Promise<ShapeSearchResult | undefined>;
}

/**
 * 5 つのデータソースを、検索チェーンから同じ形で呼べる lookup に揃えるフック。
 *
 * 各ソースのエラー時の振る舞い（内部で握りつぶすか、呼び出し側へ投げるか）は既存の
 * API フックの挙動をそのまま保つ。GEOSPACE の住居表示検索だけは 400 応答を
 * 呼び出し側のエラーハンドリングへ通す必要があるため、400 の例外だけ伝播させる。
 * 登記所備付地図の検索 3 本のレート制限（429）は rateLimited にしてチェーンを止める。
 */
export const useParcelSearchSources = (): ParcelSearchSources => {
  const {
    geospaceSearch,
    geospaceReverse,
    geospaceLegalAffairsSearch,
    geospaceLegalAffairsReverse,
    geospaceReverseByLatLng,
    geospaceLegalAffairsReverseByLatlng,
    geospaceReverseByPolygon,
    geospaceReverseByCircle,
    geospaceLegalAffairsReverseByPolygon,
    geospaceLegalAffairsReverseByCircle,
  } = useGeoSpaceApi();
  const {
    zenrinSearch,
    zenrinReverseByAddress,
    zenrinReverseByLatLng,
    zenrinReverseByPolygon,
    zenrinReverseByCircle,
  } = useZenrinApi();
  const {
    trustartSearch,
    trustartReverse,
    trustartReverseByLatLng,
    trustartReverseByPolygon,
    trustartReverseByCircle,
  } = useTrustartParcelApi();
  const {
    search: registrationMapSearch,
    reverse: registrationMapReverse,
    reverseByLatLng: registrationMapReverseByLatLng,
    reverseByPolygon: registrationMapReverseByPolygon,
    reverseByCircle: registrationMapReverseByCircle,
  } = useRegistrationMapApi();

  const trustartEnvelope = (
    response: TrustartLookupResponse | undefined
  ): SourceOutcome =>
    envelopeToOutcome<TrustartParcelResult>(
      response,
      trustartParcelToGeoSpaceChiban
    );

  const registrationMapEnvelope = (
    response: RegistrationMapLookupResponse | undefined
  ): SourceOutcome =>
    envelopeToOutcome<RegistrationMapDetail>(
      response,
      registrationMapDetailToGeoSpaceChiban
    );

  const buildLatLngLookup = (
    lat: number,
    lng: number
  ): Partial<Record<SearchSourceId, SourceLookup>> => ({
    geospaceChiban: async () =>
      toOutcome(await geospaceReverseByLatLng(lat, lng)),
    geospaceLegalAffairs: async () =>
      toOutcome(await geospaceLegalAffairsReverseByLatlng(lat, lng)),
    trustartChiban: async () =>
      trustartEnvelope(await trustartReverseByLatLng(lat, lng)),
    registrationMap: async () =>
      await catchRateLimit(async () =>
        registrationMapEnvelope(await registrationMapReverseByLatLng(lat, lng))
      ),
    zenrin: async () => {
      const result = await zenrinReverseByLatLng(lat, lng);
      return toOutcome(result ? zenrinToGeoSpaceChiban(result) : undefined);
    },
  });

  const buildChibanLookup = (
    chiban: string,
    options?: { onZenrinRejected?: (message: string) => void }
  ): Partial<Record<SearchSourceId, SourceLookup>> => ({
    geospaceChiban: async () => toOutcome(await geospaceSearch(chiban)),
    geospaceLegalAffairs: async () =>
      toOutcome(await geospaceLegalAffairsSearch(chiban)),
    trustartChiban: async () => trustartEnvelope(await trustartSearch(chiban)),
    registrationMap: async () =>
      await catchRateLimit(async () =>
        registrationMapEnvelope(await registrationMapSearch(chiban))
      ),
    zenrin: async () => {
      // ゼンリンは検索語の制約が厳しいため、叩く前に弾いて利用者に理由を伝える
      const validation = validateZenrinWordQuery(chiban);
      if (!validation.ok) {
        options?.onZenrinRejected?.(validation.message);
        return { kind: "miss" };
      }
      const result = await zenrinSearch(chiban);
      return toOutcome(result ? zenrinToGeoSpaceChiban(result) : undefined);
    },
  });

  const buildAddressLookup = (
    address: string,
    options?: { onZenrinRejected?: (message: string) => void }
  ): Partial<Record<SearchSourceId, SourceLookup>> => {
    // 役所系 2 ソースの住所検索は、BE でどちらも同じ住所を Google でジオコーディングする。
    // 片方が失敗したら、もう片方も同じ結果になるので呼ばない。
    let officeGeocodeFailed = false;
    const officeReverse =
      (reverse: () => Promise<SourceOutcome>): SourceLookup =>
      async () => {
        if (officeGeocodeFailed) return { kind: "geocodeFailed" };
        const outcome = await reverse();
        if (outcome.kind === "geocodeFailed") officeGeocodeFailed = true;
        return outcome;
      };

    return {
      // GEOSPACE の住居表示検索は 400 だけ呼び出し側のエラーハンドリング
      // （入力内容の案内文言）へ通し、それ以外のエラーは後段へ流す
      geospaceChiban: async () =>
        await passThroughBadRequest(async () =>
          toOutcome(await geospaceReverse(address))
        ),
      geospaceLegalAffairs: async () =>
        await passThroughBadRequest(async () =>
          toOutcome(await geospaceLegalAffairsReverse(address))
        ),
      trustartChiban: officeReverse(async () =>
        trustartEnvelope(await trustartReverse(address))
      ),
      registrationMap: officeReverse(
        async () =>
          await catchRateLimit(async () =>
            registrationMapEnvelope(await registrationMapReverse(address))
          )
      ),
      zenrin: async () => {
        // 登記地番検索と同じく、叩く前に弾いて利用者に理由を伝える
        const validation = validateZenrinWordQuery(address);
        if (!validation.ok) {
          options?.onZenrinRejected?.(validation.message);
          return { kind: "miss" };
        }
        const result = await zenrinReverseByAddress(address);
        return toOutcome(result ? zenrinToGeoSpaceChiban(result) : undefined);
      },
    };
  };

  const runShapeSearch = async (
    sourceId: SearchSourceId,
    shape: SearchShape,
    signal?: AbortSignal
  ): Promise<ShapeSearchResult | undefined> => {
    if (shape.kind === "circle") {
      const { radius, lat, lng } = shape;
      switch (sourceId) {
        case "geospaceChiban":
          return untruncated(
            await geospaceReverseByCircle(radius, lat, lng, signal)
          );
        case "geospaceLegalAffairs":
          return untruncated(
            await geospaceLegalAffairsReverseByCircle(radius, lat, lng, signal)
          );
        case "trustartChiban":
          return untruncated(
            await trustartReverseByCircle(radius, lat, lng, signal)
          );
        case "registrationMap":
          return await registrationMapReverseByCircle(radius, lat, lng, signal);
        case "zenrin":
          return untruncated(
            zenrinListToGeoSpaceChiban(
              await zenrinReverseByCircle(lat, lng, radius, signal)
            )
          );
      }
    }

    const { polygon } = shape;
    switch (sourceId) {
      case "geospaceChiban":
        return untruncated(await geospaceReverseByPolygon(polygon, signal));
      case "geospaceLegalAffairs":
        return untruncated(
          await geospaceLegalAffairsReverseByPolygon(polygon, signal)
        );
      case "trustartChiban":
        return untruncated(await trustartReverseByPolygon(polygon, signal));
      case "registrationMap":
        return await registrationMapReverseByPolygon(polygon, signal);
      case "zenrin":
        return untruncated(await zenrinReverseByPolygon(polygon, signal));
    }
  };

  return {
    buildLatLngLookup,
    buildChibanLookup,
    buildAddressLookup,
    runShapeSearch,
  };
};
